import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, CalendarCheck, FileText } from "lucide-react";
import Seo from "../components/Seo";
import BookingCalendar from "../components/BookingCalendar";
import { Reveal, RevealGroup, RevealItem } from "../components/Reveal";
import { BOOKING_SERVICES, BUSINESS } from "../lib/site";

const serviceId = (name) => `book-online-service-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

export default function BookOnline() {
  return (
    <>
      <Seo
        title="Book a Consultation Online | Directive Optive Solutions"
        description="Book a consultation online — Monday to Friday, 9 AM to 5 PM Mountain Time. Prefer to talk first? Call (801) 474-5890 or email info@directiveoptivesolutions.com."
        path="/book-online"
      />
      <section className="bg-sage/85 pb-12 pt-36 sm:pb-16 sm:pt-44 lg:pt-48" data-testid="book-online-header">
        <div className="container-shell">
          <Reveal>
            <p className="eyebrow">Book or ask</p>
            <h1 className="mt-5 max-w-3xl text-balance font-serif text-4xl leading-[1.08] tracking-tight text-olive sm:text-5xl lg:text-6xl">Book a consultation online.</h1>
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-moss sm:text-lg">Pick a day and a time that suit you. Consultations run Monday to Friday, 9 AM to 5 PM Mountain Time — choose a slot, tell us a little about yourself, and you’re set.</p>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 pt-14 lg:pb-28 lg:pt-20" data-testid="book-online-options">
        <div className="container-shell grid gap-10 lg:grid-cols-[1.25fr_0.75fr] lg:items-start">
          <Reveal className="min-w-0">
            <BookingCalendar dataTestId="book-online-calendar" />
          </Reveal>
          <div className="grid gap-10">
            <Reveal delay={0.1}>
              <div className="rounded-md border border-olive/25 bg-white p-6 sm:p-8">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-sage text-olive"><FileText className="h-5 w-5" /></span>
                <h2 className="mt-5 font-serif text-2xl text-olive">Prefer a quote first?</h2>
                <p className="mt-3 text-sm leading-relaxed text-moss sm:text-base">Send a short note about what you need. We reply personally with a clear scope and quote. No sales scripts and no obligation.</p>
                <Link to="/contact" data-testid="book-online-quote-button" className="btn-outline mt-6">
                  Request a quote <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
            <Reveal delay={0.16}>
              <div className="rounded-md border border-olive/25 bg-sage p-6 sm:p-8" data-testid="book-online-expectations">
                <h2 className="font-serif text-2xl text-olive">What to expect</h2>
                <ul className="mt-4 space-y-3 text-sm leading-relaxed text-moss">
                  <li className="flex gap-3"><CalendarCheck className="h-4 w-4 shrink-0 text-pine" /> You talk directly with the person who will handle your books.</li>
                  <li className="flex gap-3"><CalendarCheck className="h-4 w-4 shrink-0 text-pine" /> The first conversation is simply about where you are and what you need.</li>
                  <li className="flex gap-3"><CalendarCheck className="h-4 w-4 shrink-0 text-pine" /> You leave with clear next steps — even if the next step is only “let’s not rush.”</li>
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="pb-20 lg:pb-28" data-testid="book-online-services">
        <div className="container-shell">
          <Reveal className="max-w-3xl">
            <h2 className="font-serif text-2xl text-olive sm:text-3xl">Or book a specific engagement directly</h2>
          </Reveal>
          <RevealGroup className="mt-8 border-b border-line">
            {BOOKING_SERVICES.map((s) => {
              const slug = s.url.split("/booking-calendar/")[1];
              return (
                <RevealItem key={s.name}>
                  <Link to={`/booking-calendar/${slug}`} data-testid={serviceId(s.name)} className="group grid gap-2 border-t border-line py-6 transition-colors hover:bg-sage/60 sm:grid-cols-[1fr_auto] sm:items-center sm:gap-6 sm:px-4">
                    <div>
                      <h3 className="font-serif text-xl text-olive sm:text-2xl">{s.name}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-moss">{s.blurb}</p>
                    </div>
                    <span className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-pine sm:inline-flex">
                      Book now <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </Link>
                </RevealItem>
              );
            })}
          </RevealGroup>
          <Reveal delay={0.1} className="mt-12">
            <p className="text-sm font-semibold text-olive">
              Prefer to talk first? Call <a href={BUSINESS.phoneHref} data-testid="book-online-phone-link" className="underline decoration-olive/40 underline-offset-4 hover:text-pine">{BUSINESS.phone}</a> or email <a href={BUSINESS.emailHref} data-testid="book-online-email-link" className="underline decoration-olive/40 underline-offset-4 hover:text-pine">{BUSINESS.email}</a>
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
